import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Text } from 'rizzui';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import { menuRoutes } from '../../../router/menu';
import { FacilitatorCallsService } from '../../../services/calls/facilitatorCallsService';

const panelClassName = 'rounded-md border border-gray-200 bg-white p-4';

const FacilitatorManagerDashboard = () => {
    const service = new FacilitatorCallsService();
    const { userCan } = usePrivilege();
    const canViewPendingExtras = userCan('show_all_pending_contract_extras', 'contracts');

    const [assignmentsResponse, assignmentsLoading] = useFetch(useCallback(async () => {
        const response = await service.listSystemFacilitators({ page: 1, limit: 8 });
        return response.getResponseData();
    }, []));

    const [pendingExtrasResponse] = useFetch(useCallback(async () => {
        if (!canViewPendingExtras) {
            return {
                success: true,
                data: {
                    data: [],
                },
            };
        }

        const response = await service.listPendingContractExtras({ page: 1, limit: 5 });
        return response.getResponseData();
    }, [canViewPendingExtras]));

    const summary = assignmentsResponse?.summary ?? {
        facilitatorsTotal: 0,
        assignedContracts: 0,
        totalCalls: 0,
        totalRealizedCalls: 0,
        totalPendingCalls: 0,
    };
    const assignmentRows = assignmentsResponse?.data ?? [];
    const pendingRows = pendingExtrasResponse?.data ?? [];

    if (assignmentsLoading && !assignmentsResponse) {
        return <Loader height="50vh" />;
    }

    return (
        <div className="grid grid-cols-1 gap-6 @3xl/crm:grid-cols-12">
            <div className="@3xl/crm:col-span-8 space-y-6">
                <div className="grid gap-4 md:grid-cols-5">
                    {[
                        { label: 'Dinamizadores', value: summary.facilitatorsTotal },
                        { label: 'Contratos activos', value: summary.assignedContracts },
                        { label: 'Llamadas', value: summary.totalCalls },
                        { label: 'Realizadas', value: summary.totalRealizedCalls },
                        { label: 'Pendientes', value: summary.totalPendingCalls },
                    ].map((card) => (
                        <div key={card.label} className={panelClassName}>
                            <Text className="text-xs uppercase text-gray-500">{card.label}</Text>
                            <Text className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</Text>
                        </div>
                    ))}
                </div>

                <div className={panelClassName}>
                    <div className="flex items-center justify-between gap-3">
                        <Text className="text-base font-semibold text-gray-900">Asignaciones</Text>
                        <Link className="text-sm font-semibold text-primary" to={menuRoutes.calls.path + menuRoutes.calls.assignments}>
                            Abrir listado
                        </Link>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr className="text-left text-xs font-semibold uppercase text-gray-500">
                                    <th className="px-3 py-2">Dinamizador</th>
                                    <th className="px-3 py-2">Email</th>
                                    <th className="px-3 py-2">Total</th>
                                    <th className="px-3 py-2">Realizadas</th>
                                    <th className="px-3 py-2">Pendientes</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {assignmentRows.map((row: any) => (
                                    <tr key={row.facilitatorUserId}>
                                        <td className="px-3 py-2">{[row.facilitatorUserName, row.facilitatorUserLastName].filter(Boolean).join(' ')}</td>
                                        <td className="px-3 py-2">{row.facilitatorUserEmail || 'N/A'}</td>
                                        <td className="px-3 py-2">{row.totalCalls || 0}</td>
                                        <td className="px-3 py-2">{row.totalRealizedCalls || 0}</td>
                                        <td className="px-3 py-2">{row.totalPendingCalls || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {canViewPendingExtras && (
                <div className="@3xl/crm:col-span-4">
                    <div className={panelClassName}>
                        <div className="flex items-center justify-between gap-3">
                            <Text className="text-base font-semibold text-gray-900">Extras pendientes</Text>
                            <Link className="text-sm font-semibold text-primary" to={menuRoutes.calls.path + menuRoutes.calls.pendingExtras}>
                                Abrir bandeja
                            </Link>
                        </div>
                        <div className="mt-4 space-y-3">
                            {pendingRows.length === 0 && (
                                <Text className="text-sm text-gray-500">No hay extras pendientes ahora mismo.</Text>
                            )}
                            {pendingRows.map((row: any) => (
                                <div key={row.id} className="rounded border border-gray-200 p-3">
                                    <Text className="text-sm font-semibold text-gray-900">{row.product?.name || row.title || 'Curso'}</Text>
                                    <Text className="mt-1 text-sm text-gray-600">{[row.name, row.firstName, row.lastName].filter(Boolean).join(' ') || 'Sin nombre'}</Text>
                                    <Text className="mt-1 text-xs text-gray-500">{row.contract?.code || 'Contrato'} · {row.documentNumber || 'Sin DNI'}</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FacilitatorManagerDashboard;
